import { CreateOfferEditModel } from "./../../../node_modules/@playcharge/common/models/backend/offers/backoffice/create-offer-edit-model";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { ApiUtils } from "../utils/api-utils";
import { ApiRoutes } from "../models/constants/api-routes";
import { Observable } from "rxjs";
import { BackOfficeOfferViewModel } from "@playcharge/common/models/backend/offers/backoffice/backoffice-offer-view-model";
import { UpdateOfferEditModel } from "@playcharge/common/models/backend/offers/backoffice/update-offer-edit-model";
import { OfferType } from "@playcharge/common/models/backend/offers/offer-type";
import { Platform } from "@playcharge/common/models/backend/auth/platform";
import { UpdateOfferGoalsEditModel } from "@playcharge/common/models/backend/offers/backoffice/update-offer-goals-edit-model";
import { CopyOfferDataEditModel } from "@playcharge/common/models/backend/offers/backoffice/copy-offer-data-edit-model";
import { UpdateOfferChallengeBlocksEditModel } from "@playcharge/common/models/backend/offers/backoffice/update-offer-challenge-blocks-edit-model";
import { OfferStatisticPreviewViewModel } from "@playcharge/common/models/backend/offers/backoffice/offer-statistic-preview-view-model";
import { DateTime } from "luxon";

@Injectable({
    providedIn: "root",
})
export class ApiOffersService {
    private _httpClient = inject(HttpClient);

    public getAll(
        offerType: OfferType,
        isArchived: boolean,
    ): Observable<BackOfficeOfferViewModel[]> {
        let queryParams: HttpParams = new HttpParams();
        queryParams = queryParams.append("offerType", offerType);
        queryParams = queryParams.append("isArchived", isArchived);
        queryParams = queryParams.append("platform", Platform.All);

        return this._httpClient.get<BackOfficeOfferViewModel[]>(
            ApiUtils.buildUrl(ApiRoutes.offers.root),
            { params: queryParams },
        );
    }

    public getById(offerId: number): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.get<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(
                ApiRoutes.offers.offer,
                new Map<string, string>([["offerId", offerId.toString()]]),
            ),
        );
    }

    public publish(offerId: number): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.publish, new Map([["offerId", offerId.toString()]])),
            null,
        );
    }

    public archive(offerId: number): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.archive, new Map([["offerId", offerId.toString()]])),
            null,
        );
    }

    public createOffer(model: CreateOfferEditModel): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.root),
            model,
        );
    }

    public updateOffer(model: UpdateOfferEditModel): Observable<BackOfficeOfferViewModel> {
        const formData: FormData = new FormData();

        Object.entries(model).forEach((x) => {
            if (x[1]) {
                if (Array.isArray(x[1])) {
                    if (x[1].length) {
                        for (const item of x[1]) {
                            formData.append(x[0], item);
                        }
                    }
                } else {
                    formData.append(x[0], x[1]);
                }
            }
        });

        return this._httpClient.put<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.root),
            formData,
        );
    }

    public updateOfferGoals(
        offerId: number,
        model: UpdateOfferGoalsEditModel,
    ): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.goals, new Map([["offerId", offerId.toString()]])),
            model,
        );
    }

    public copyOffer(model: CopyOfferDataEditModel): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.copyOffer),
            model,
        );
    }

    public copyFullOffer(model: CopyOfferDataEditModel): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.copyFullOffer, new Map([["offerId", model.sourceOfferId.toString()]])),
            null,
        );
    }

    public validateOffer(model: CopyOfferDataEditModel): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.get<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.validateOffer, new Map([["offerId", model.sourceOfferId.toString()]])),
        );
    }

    public activateOffer(model: CopyOfferDataEditModel): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.activateOffer, new Map([["offerId", model.sourceOfferId.toString()]])), null
        );
    }

    public deactivateOffer(model: CopyOfferDataEditModel): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.deactivateOffer, new Map([["offerId", model.sourceOfferId.toString()]])), null
        );
    }

    public copyOfferGoals(model: CopyOfferDataEditModel): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(ApiRoutes.offers.copyGoals),
            model,
        );
    }

    public uploadIdfaCsv(file: File): Observable<void> {
        const formData = new FormData();
        formData.append("file", file);

        return this._httpClient.post<void>(
            ApiUtils.buildUrl(ApiRoutes.offers.uploadIdfaCsv),
            formData,
        );
    }

    public updateOfferChallengeBlocks(
        offerId: number,
        model: UpdateOfferChallengeBlocksEditModel,
    ): Observable<BackOfficeOfferViewModel> {
        return this._httpClient.post<BackOfficeOfferViewModel>(
            ApiUtils.buildUrl(
                ApiRoutes.offers.challenges,
                new Map([["offerId", offerId.toString()]]),
            ),
            model,
        );
    }

    public getStatisticsByRange(
        startDate: DateTime,
        endDate: DateTime,
    ): Observable<OfferStatisticPreviewViewModel[]> {
        let queryParams: HttpParams = new HttpParams();
        queryParams = queryParams.append("startDate", startDate.toISO()!);
        queryParams = queryParams.append("endDate", endDate.toISO()!);

        return this._httpClient.get<OfferStatisticPreviewViewModel[]>(
            ApiUtils.buildUrl(ApiRoutes.offers.stats),
            { params: queryParams },
        );
    }
}
